import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { SidebarNav } from './SidebarNav';
import { BrandLogo } from './BrandLogo';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { NavigationItem } from '@/types/navigation';

interface SidebarProps {
  isMobileOpen: boolean;
  onMobileClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  navItems?: NavigationItem[];
}

export const Sidebar: React.FC<SidebarProps> = ({
  isMobileOpen,
  onMobileClose,
  isCollapsed,
  onToggleCollapse,
  navItems,
}) => {
  const { t } = useTranslation('layout');

  return (
    <>
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col overflow-hidden bg-card border-r transition-all duration-300 ease-in-out',
          'lg:relative lg:translate-x-0',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full',
          isCollapsed ? 'lg:w-20' : 'lg:w-60'
        )}
      >
        <div
          className={cn(
            'flex h-16 items-center justify-between border-b',
            isCollapsed ? 'px-1' : 'px-4'
          )}
        >
          <Link
            to="/"
            onClick={onMobileClose}
            className={cn(
              'flex min-w-0 items-center rounded-md outline-none transition-colors',
              'hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              !isCollapsed && 'flex-1',
              isCollapsed && 'shrink-0 justify-center'
            )}
            aria-label={t('brand.name')}
          >
            {!isCollapsed && <BrandLogo className="h-9" />}
            {isCollapsed && <BrandLogo compact />}
          </Link>

          <button
            onClick={onMobileClose}
            className="p-2 lg:hidden"
            aria-label={t('sidebar.closeMenu')}
          >
            <X size={24} />
          </button>

          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            className={cn('hidden shrink-0 lg:flex', isCollapsed && 'h-8 w-8')}
            aria-label={isCollapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <SidebarNav
            isCollapsed={isCollapsed}
            onItemClick={onMobileClose}
            {...(navItems !== undefined ? { items: navItems } : {})}
          />
        </div>

        {isCollapsed && (
          <div className="mt-auto border-t p-2 hidden lg:block">
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              className="w-full"
              aria-label={t('sidebar.expandSidebar')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </aside>
    </>
  );
};
